import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Grid, Stack, TextField, Typography } from '@mui/material';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useBreakpoints } from 'hooks';
import { UsersApi } from 'services/users/users.api';

import { Button } from 'components/_basic/Button/Button';
import { FormField } from 'components/_basic/Form/FormField';
import { ModalComponent } from 'components/_basic/ModalComponent/ModalComponent';
import { PasswordTextField } from 'components/_basic/PasswordTextField/PasswordTextField';
import { IAuthProviderProps } from 'components/AuthProvider/types';

import ToastService from 'helpers/ToastService';

import { accessFormInitialValues, eightHours, IAccessProviderInitialValues } from './types';

import styles from './AccessProvider.module.scss';

export const AccessProvider: React.FC<IAuthProviderProps> = ({ children }) => {
  const [hasAccess, setHasAccess] = useState(false);
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();
  const accessWorkerRef = useRef<Worker | null>(null);
  const lastLoginDate = localStorage.getItem('devLoginDate');

  const now = new Date();
  const isLastLessThanEightHours = lastLoginDate && now.getTime() - new Date(lastLoginDate).getTime() < eightHours;

  useEffect(() => {
    if (hasAccess) {
      accessWorkerRef.current = new Worker(new URL('components/AccessProvider/AccessWorker.js', import.meta.url));
      accessWorkerRef.current.onmessage = (e) => {
        if (e.data === 'clearDevLoginTime') {
          localStorage.removeItem('devLoginDate');
          setHasAccess(false);
        }
      };
    }
    return () => {
      accessWorkerRef.current?.terminate();
    };
  }, [hasAccess]);

  if (isLastLessThanEightHours) {
    return <>{children}</>;
  }

  const onSubmit = async ({ userName, password }: IAccessProviderInitialValues) => {
    try {
      await UsersApi.verifyAccess({ userName, password });
      localStorage.setItem('devLoginDate', new Date().toString());
      setHasAccess(true);
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  };

  return (
    <ModalComponent open={true} fullScreen={isMobile}>
      <Box className={styles['content']}>
        <Box className={styles.loginWrapper}>
          <Grid container alignItems="center" mb={{ xs: 5, sm: 3 }}>
            <Grid item xs={11}>
              <Typography variant="h1" className={styles.loginFormTitle}>
                Dev Log in
              </Typography>
            </Grid>
          </Grid>
          <Formik initialValues={accessFormInitialValues} onSubmit={onSubmit}>
            <Stack spacing={3} component={Form}>
              <Field name="userName">
                {({ field, meta }: FieldProps<string>) => (
                  <FormField id={'userNam'} label={t('Email')} error={meta.touched && meta.error}>
                    <TextField {...field} placeholder={t('enterYourEmail')} />
                  </FormField>
                )}
              </Field>
              <Box>
                <Field name="password">
                  {({ field, meta }: FieldProps<string>) => (
                    <FormField id={'password'} label={t('password')} error={meta.touched && meta.error}>
                      <PasswordTextField
                        {...field}
                        autoComplete="on"
                        placeholder={t('enterYourPassword')}
                        inputProps={{ maxLength: 30 }}
                      />
                    </FormField>
                  )}
                </Field>
              </Box>
              <Button fullWidth type="submit" variant="contained" size={isMobile ? 'large' : 'medium'}>
                {t('logIn')}
              </Button>
            </Stack>
          </Formik>
        </Box>
      </Box>
    </ModalComponent>
  );
};
